import { useCallback } from 'react'

type useAudioSpectrumProps = {
  canvasRef: React.RefObject<HTMLCanvasElement | null>
  analyserRef: React.RefObject<AnalyserNode | null>
  animationRef: React.MutableRefObject<number | null>
  barColor?: string
  backgroundColor?: string
  barWidth?: number
  barGap?: number
}

const BACKGROUND_COLOR = '#ffffff'
const BAR_COLOR = '#1a6fb5'
const BAR_WIDTH = 5
const BAR_GAP = 16

const useAudioSpectrum = ({
  canvasRef,
  analyserRef,
  animationRef,
  barColor = BAR_COLOR,
  backgroundColor = BACKGROUND_COLOR,
  barWidth = BAR_WIDTH,
  barGap = BAR_GAP
}: useAudioSpectrumProps) => {
  const drawSpectrum = useCallback(() => {
    const canvas = canvasRef.current
    const analyser = analyserRef.current
    if (!canvas || !analyser) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const bufferLength = analyser.frequencyBinCount
    const dataArray = new Uint8Array(bufferLength)

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw)
      analyser.getByteFrequencyData(dataArray)

      ctx.fillStyle = backgroundColor
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      const numBars = 16
      const centerY = canvas.height / 2
      const totalWidth = numBars * barWidth + (numBars - 1) * barGap
      const startX = (canvas.width - totalWidth) / 2

      ctx.fillStyle = barColor

      for (let i = 0; i < numBars; i++) {
        const t = (i / (numBars - 1)) * 2 - 1
        const gaussian = Math.exp(-t * t * 2.2)

        const dataIndex = Math.floor((i / numBars) * bufferLength * 0.6)
        const rawAmplitude = dataArray[dataIndex] / 255
        const amplitude = rawAmplitude * gaussian

        const barHeight = Math.max(2, amplitude * centerY * 1.8)
        const x = startX + i * (barWidth + barGap)

        ctx.fillRect(x, centerY - barHeight, barWidth, barHeight * 2)
      }
    }

    draw()
  }, [])

  return {
    drawSpectrum
  }
}

export default useAudioSpectrum
